import React, { useEffect, useState } from "react";
import meta from "../../../package.json";
import { useMsal } from "@azure/msal-react";
import { InteractionStatus } from "@azure/msal-browser";
import { msalLoginRequest } from "Common/msal/msalConfig";
import { ssoLoginUser } from "slices/thunk";
import { useDispatch, useSelector } from "react-redux";
import withRouter from "Common/withRouter";
import { createSelector } from "reselect";
import AuthIcon from "pages/AuthenticationInner/AuthIcon";
import LoaderComponent from "Common/Components/Loader/Loader";
import { ToastContainer } from "react-toastify";
import { showNotification } from "Common/utils/Notification";
import Modal from "Common/Components/Modal";
import {
  loginRequest,
  clearLoginNotifications,
  setAccessDenied,
} from "slices/auth/login/reducer";

// Lives outside the component so it survives StrictMode's effect re-run. It resets
// on every full page load, which is when a Microsoft redirect result arrives.
let redirectHandled = false;

const Login = (props: any) => {
  document.title = "Login | Indxx";
  const dispatch = useDispatch<any>();
  const { instance, inProgress } = useMsal();
  // Starts true: on every mount we first need to check whether this page load
  // is Microsoft redirecting back to us with a login result.
  const [loader, setLoader] = useState<boolean>(true);

  const selectLogin = createSelector(
    (state: any) => state.Login,
    (login) => ({
      success: login.success,
      error: login.error,
      isLoginAction: login.isLoginAction,
      accessDenied: login.accessDenied,
    })
  );

  const { success, error, isLoginAction, accessDenied } = useSelector(selectLogin);

  // Clear login state when component mounts
  useEffect(() => {
    dispatch(loginRequest());
  }, [dispatch]);

  // Explain why the user was sent back here after a replaced or ended session.
  useEffect(() => {
    if (window.sessionStorage.getItem("session_revoked")) {
      window.sessionStorage.removeItem("session_revoked");
      showNotification(
        "You were signed out because this account signed in elsewhere, or the session ended.",
        "warning"
      );
    }
  }, []);

  // Confirm a deliberate logout.
  useEffect(() => {
    if (window.sessionStorage.getItem("logged_out")) {
      window.sessionStorage.removeItem("logged_out");
      showNotification("Logged out successfully", "success");
    }
  }, []);

  // Process the result of a redirect back from Microsoft, if this page load is one.
  useEffect(() => {
    // StrictMode runs effects twice in dev; a second login would replace the
    // first one's session id and invalidate the token we just stored.
    if (redirectHandled) {
      setLoader(false);
      return;
    }
    redirectHandled = true;

    instance
      .handleRedirectPromise()
      .then(async (response) => {
        if (response?.idToken) {
          await dispatch(ssoLoginUser(response.idToken, props.router.navigate));
        }
      })
      .catch((err: any) => {
        showNotification(
          err?.message || "Microsoft sign-in was cancelled or failed.",
          "error"
        );
      })
      .finally(() => {
        setLoader(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Add body classes on mount
  useEffect(() => {
    const bodyElement = document.body;

    bodyElement.classList.add(
      "flex",
      "items-center",
      "justify-center",
      "min-h-screen",
      "py-16",
      "lg:py-10",
      "bg-slate-50",
      "dark:bg-zink-800",
      "dark:text-zink-100",
      "font-public"
    );

    return () => {
      bodyElement.classList.remove(
        "flex",
        "items-center",
        "justify-center",
        "min-h-screen",
        "py-16",
        "lg:py-10",
        "bg-slate-50",
        "dark:bg-zink-800",
        "dark:text-zink-100",
        "font-public"
      );
    };
  }, []);

  // Handle notifications when success or error state changes
  useEffect(() => {
    if (error || success) {
      setLoader(false);
    }

    if (error) {
      const errorMessage =
        typeof error === "string"
          ? error
          : error?.data?.message || error?.message || "An error occurred";

      showNotification(errorMessage, "error");

      const timeoutId = setTimeout(() => {
        dispatch(clearLoginNotifications());
      }, 100);

      return () => clearTimeout(timeoutId);
    } else if (success && isLoginAction) {
      showNotification("Login successfully", "success");

      const timeoutId = setTimeout(() => {
        dispatch(clearLoginNotifications());
      }, 100);

      return () => clearTimeout(timeoutId);
    }
  }, [success, error, isLoginAction, dispatch]);

  const handleMicrosoftLogin = () => {
    if (loader || inProgress !== InteractionStatus.None) {
      return;
    }

    setLoader(true);

    // Navigates the whole page to Microsoft's login screen. On success, the
    // browser is redirected back to this same page, handled by the effect above.
    instance.loginRedirect(msalLoginRequest).catch((err: any) => {
      setLoader(false);
      showNotification(
        err?.message || "Unable to start Microsoft sign-in.",
        "error"
      );
    });
  };

  const handleAccessDeniedClose = () => {
    dispatch(setAccessDenied(false));
  };

  return (
    <React.Fragment>
      <div className="relative">
        <AuthIcon />
        <ToastContainer />
        <div className="mb-0 w-screen lg:mx-auto lg:w-[500px] card shadow-lg border-none shadow-slate-100 relative">
          <div className="!px-10 !py-12 card-body">
            <div className="mt-8 text-center">
              <h4 className="mb-1 text-custom-500 dark:text-custom-500">
                Welcome Back !
              </h4>
              <p className="text-slate-500 dark:text-zink-200">
                Sign in to continue to Indxx.
              </p>
            </div>

            <div className="mt-10">
              <button
                type="button"
                disabled={loader}
                onClick={handleMicrosoftLogin}
                style={{
                  minWidth: "130px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "10px",
                }}
                className="w-full text-white btn bg-custom-500 border-custom-500 hover:text-white hover:bg-custom-600 hover:border-custom-600 focus:text-white focus:bg-custom-600 focus:border-custom-600 focus:ring focus:ring-custom-100 active:text-white active:bg-custom-600 active:border-custom-600 active:ring active:ring-custom-100 dark:ring-custom-400/20"
              >
                {loader ? (
                  <LoaderComponent />
                ) : (
                  <>
                    <svg width="18" height="18" viewBox="0 0 21 21" aria-hidden="true">
                      <rect x="1" y="1" width="9" height="9" fill="#f25022" />
                      <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
                      <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
                      <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
                    </svg>
                    Sign in with Microsoft
                  </>
                )}
              </button>

              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  padding: "10px 0",
                  color: "#64748b",
                }}
              >
                {`v.${meta.version}`}
              </div>
            </div>
          </div>
        </div>
      </div>

      <Modal
        show={accessDenied}
        onHide={handleAccessDeniedClose}
        id="accessDeniedModal"
        modal-center="true"
        className="fixed flex flex-col transition-all duration-300 ease-in-out left-2/4 z-drawer -translate-x-2/4 -translate-y-2/4"
        dialogClassName="w-screen md:w-[25rem] bg-white shadow rounded-md dark:bg-zink-600"
      >
        <Modal.Body className="px-6 py-8">
          <div className="text-center">
            <h5 className="mb-1">Access Denied</h5>
            <p className="text-slate-500 dark:text-zink-200">
              This Microsoft account is not registered as an admin for this
              application. Please contact your administrator if you believe
              this is a mistake.
            </p>
            <div className="flex justify-center mt-6">
              <button
                type="button"
                className="text-white btn bg-custom-500 border-custom-500 hover:text-white hover:bg-custom-600 hover:border-custom-600"
                onClick={handleAccessDeniedClose}
              >
                OK
              </button>
            </div>
          </div>
        </Modal.Body>
      </Modal>
    </React.Fragment>
  );
};

export default withRouter(Login);
